import { ReactNode, useEffect, useRef, useState } from "react";

interface ParallaxProps {
  children: ReactNode;
  speed?: number; // 0..1, fraction of scroll translated
  className?: string;
  disableMobile?: boolean;
}

/**
 * Light vertical parallax. Translates children based on element offset.
 * Disabled on touch / reduced motion / mobile by default.
 */
export const Parallax = ({ children, speed = 0.18, className = "", disableMobile = true }: ParallaxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (reduced || (disableMobile && isMobile)) { setEnabled(false); return; }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const node = ref.current;
        if (node) {
          const rect = node.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const fromCenter = center - window.innerHeight / 2;
          setOffset(fromCenter * -speed);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed, disableMobile]);

  return (
    <div ref={ref} className={className} style={{ willChange: enabled ? "transform" : undefined }}>
      <div
        className="w-full h-full"
        style={{
          transform: enabled ? `translate3d(0, ${offset}px, 0)` : undefined,
          transition: "transform 80ms linear",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Parallax;